// SharePoint configuration with validation
const getSharePointConfig = () => {
  const siteUrl = process.env.REACT_APP_SHAREPOINT_SITE_URL || 'https://sunbethconcepts.sharepoint.com/sites/Procurement';
  
  // Validate the site URL
  if (!siteUrl || typeof siteUrl !== 'string') {
    console.error('Invalid SharePoint site URL from environment:', process.env.REACT_APP_SHAREPOINT_SITE_URL);
    throw new Error('REACT_APP_SHAREPOINT_SITE_URL environment variable is not set correctly');
  }
  
  return {
    siteUrl: siteUrl.trim(),
  listName: process.env.REACT_APP_SHAREPOINT_LIST_NAME || 'Vendor Management',
  documentLibrary: process.env.REACT_APP_SHAREPOINT_DOC_LIBRARY || 'Vendor Documents',
};
};

const SHAREPOINT_CONFIG = getSharePointConfig();

// Debug logging for SharePoint configuration
console.log('SharePoint Configuration:', {
  siteUrl: SHAREPOINT_CONFIG.siteUrl,
  listName: SHAREPOINT_CONFIG.listName,
  documentLibrary: SHAREPOINT_CONFIG.documentLibrary,
  envSiteUrl: process.env.REACT_APP_SHAREPOINT_SITE_URL,
  allEnvVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_'))
});

// Store access token for API calls
let accessToken: string = '';

// Initialize SharePoint with MSAL token
export const initializeSharePoint = async (token: string) => {
  try {
    accessToken = token;
    
    // Validate configuration
    if (!SHAREPOINT_CONFIG.siteUrl) {
      throw new Error('SharePoint site URL is not configured. Please set REACT_APP_SHAREPOINT_SITE_URL in your .env file.');
    }
    
    if (!token) {
      throw new Error('No access token provided for SharePoint authentication.');
    }
    
    console.log('SharePoint initialized with token:', token ? 'Present' : 'Missing');
    console.log('SharePoint site URL:', SHAREPOINT_CONFIG.siteUrl);
    
    // Test the connection by getting request digest
    try {
      await getRequestDigest();
      console.log('SharePoint connection test successful');
    } catch (testError) {
      console.warn('SharePoint connection test failed:', testError);
      // Don't fail initialization, just warn
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error initializing SharePoint:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

// Helper function to make Microsoft Graph API calls for SharePoint
const makeGraphCall = async (endpoint: string, options: RequestInit = {}) => {
  const url = `https://graph.microsoft.com/v1.0${endpoint}`;
  
  console.log('Making Microsoft Graph API call to:', url);
  console.log('Access token available:', !!accessToken);
  
  if (!accessToken) {
    throw new Error('No access token available for Microsoft Graph API call');
  }
  
  try {
    const defaultOptions: RequestInit = {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/json',
      },
    };

    // Only add Content-Type if not already specified (for file uploads)
    const hasContentType = options.headers && (
      (options.headers as any)['Content-Type'] || 
      (options.headers as any)['content-type']
    );
    
    if (!hasContentType) {
      (defaultOptions.headers as any)['Content-Type'] = 'application/json';
    }

    const finalOptions = { ...defaultOptions, ...options };
    
    // Merge headers properly
    if (options.headers) {
      finalOptions.headers = { ...defaultOptions.headers, ...options.headers };
    }

    console.log('Request headers:', finalOptions.headers);
    
    const response = await fetch(url, finalOptions);
    
    console.log('Microsoft Graph API response status:', response.status);
    
  if (!response.ok) {
    const errorText = await response.text();
    console.error('Microsoft Graph API call failed:', {
      status: response.status,
      statusText: response.statusText,
      error: errorText,
      url: url,
      requestBody: options?.body
    });
    throw new Error(`Microsoft Graph API call failed: ${response.status} ${response.statusText} - ${errorText}`);
  }
    
    return response.json();
  } catch (error) {
    console.error('Microsoft Graph API call error:', error);
    throw error;
  }
};

// Get request digest for SharePoint API calls (not needed for Microsoft Graph API)
const getRequestDigest = async (): Promise<string> => {
  console.log('Request digest not needed for Microsoft Graph API calls');
  return 'not-needed-for-graph-api';
};

// Create SharePoint list if it doesn't exist
export const ensureVendorListExists = async () => {
  try {
    console.log('Ensuring SharePoint list exists with required columns...');
    
    // Extract site hostname and path from SharePoint URL
    const siteUrl = SHAREPOINT_CONFIG.siteUrl;
    console.log('Full SharePoint URL:', siteUrl);
    console.log('Site URL type:', typeof siteUrl);
    console.log('Site URL length:', siteUrl ? siteUrl.length : 'undefined');
    
    // Validate the URL before processing
    if (!siteUrl || typeof siteUrl !== 'string') {
      throw new Error(`Invalid SharePoint site URL: ${siteUrl}. Please check REACT_APP_SHAREPOINT_SITE_URL environment variable.`);
    }
    
    // Ensure the URL is properly formatted
    const cleanSiteUrl = siteUrl.trim();
    
    // Validate URL format
    if (!cleanSiteUrl.startsWith('https://')) {
      throw new Error(`Invalid SharePoint URL format: ${cleanSiteUrl}. URL must start with https://`);
    }
    
    let urlParts;
    try {
      urlParts = new URL(cleanSiteUrl);
    } catch (urlError) {
      console.error('URL construction error:', urlError);
      throw new Error(`Failed to parse SharePoint URL: ${cleanSiteUrl}. Error: ${urlError instanceof Error ? urlError.message : 'Unknown error'}`);
    }
    const hostname = urlParts.hostname;
    const sitePath = urlParts.pathname.substring(1); // Remove leading slash
    
    console.log('URL parts:', {
      hostname: urlParts.hostname,
      pathname: urlParts.pathname,
      sitePath: sitePath
    });
    
    // Validate that we have the correct path format
    if (!sitePath.startsWith('sites/')) {
      console.error('Invalid SharePoint site path format. Expected format: sites/siteName');
      throw new Error(`Invalid SharePoint site path: ${sitePath}. Expected format: sites/siteName`);
    }
    
    console.log('SharePoint hostname:', hostname);
    console.log('SharePoint site path:', sitePath);
    
    // For Microsoft Graph API, we need to use the format: hostname:/sites/siteName
    const graphSitePath = sitePath;
    
    console.log('Graph site path:', graphSitePath);
    
    // Get site by hostname and path - properly encode the site path
    const encodedSitePath = encodeURIComponent(graphSitePath);
    const siteResponse = await makeGraphCall(`/sites/${hostname}:/${encodedSitePath}`);
    const siteId = siteResponse.id;
    
    console.log('SharePoint site ID:', siteId);
    
    // Check if list exists
    const listsResponse = await makeGraphCall(`/sites/${siteId}/lists?$filter=displayName eq '${SHAREPOINT_CONFIG.listName}'`);
    
    let listId;
    if (listsResponse.value.length === 0) {
      // Create the list
      const createListData = {
        displayName: SHAREPOINT_CONFIG.listName,
        description: 'Vendor management list for PMO',
        template: 'genericList'
      };

      const listResponse = await makeGraphCall(`/sites/${siteId}/lists`, {
        method: 'POST',
        body: JSON.stringify(createListData),
      });

      listId = listResponse.id;
      console.log('SharePoint list created successfully:', listResponse);
    } else {
      listId = listsResponse.value[0].id;
      console.log('SharePoint list already exists:', listId);
    }

    // Add custom columns to the list
    console.log('About to add custom columns to list...');
    await addCustomColumnsToList(siteId, listId);
    console.log('Custom columns added successfully');

    console.log('Returning from ensureVendorListExists with:', { success: true, listId, siteId });
    return { success: true, listId, siteId };
  } catch (error) {
    console.error('Error ensuring vendor list exists:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

// Add custom columns to the SharePoint list using Microsoft Graph API
const addCustomColumnsToList = async (siteId: string, listId: string) => {
  try {
    console.log('Adding custom columns to SharePoint list...');
    
    const columns = [
      { name: 'CompanyName', displayName: 'Company Name', type: 'text', required: false },
      { name: 'Email', displayName: 'Email Address', type: 'text', required: false },
      { name: 'Telephone', displayName: 'Telephone Number', type: 'text', required: false },
      { name: 'FaxNumber', displayName: 'Fax Number', type: 'text', required: false },
      { name: 'RegistrationNumber', displayName: 'Registration Number', type: 'text', required: false },
      { name: 'TINNumber', displayName: 'TIN Number', type: 'text', required: false },
      { name: 'RegisteredAddress', displayName: 'Registered Address', type: 'text', required: false },
      { name: 'Categories', displayName: 'Service Categories', type: 'text', required: false },
      { name: 'SpecializationDetails', displayName: 'Areas of Specialization', type: 'text', required: false },
      { name: 'ExtendCredit', displayName: 'Extend Credit Facilities', type: 'text', required: false },
      { name: 'CreditVolume', displayName: 'Credit Volume', type: 'text', required: false },
      { name: 'CreditTenure', displayName: 'Credit Tenure', type: 'text', required: false },
      { name: 'PaymentPlan', displayName: 'Payment Plan', type: 'text', required: false },
      { name: 'PostDeliveryPayment', displayName: 'Post Delivery Payment', type: 'text', required: false },
      { name: 'BankingInfo', displayName: 'Banking Information (Full)', type: 'text', required: false },
      { name: 'BVN', displayName: 'Bank Verification Number (BVN)', type: 'text', required: false },
      { name: 'References', displayName: 'Client References (Full)', type: 'text', required: false },
      
      // Primary Banking Info (for searchability)
      { name: 'PrimaryBankName', displayName: 'Primary Bank Name', type: 'text', required: false },
      { name: 'PrimaryBankType', displayName: 'Primary Bank Type', type: 'text', required: false },
      { name: 'PrimaryAccountNumber', displayName: 'Primary Account Number', type: 'text', required: false },
      
      // Secondary Banking Info (for searchability)
      { name: 'SecondaryBankName', displayName: 'Secondary Bank Name', type: 'text', required: false },
      { name: 'SecondaryBankType', displayName: 'Secondary Bank Type', type: 'text', required: false },
      { name: 'SecondaryAccountNumber', displayName: 'Secondary Account Number', type: 'text', required: false },
      
      // Primary Reference Info (for searchability)
      { name: 'PrimaryReferenceCompany', displayName: 'Primary Reference Company', type: 'text', required: false },
      { name: 'PrimaryReferencePhone', displayName: 'Primary Reference Phone', type: 'text', required: false },
      { name: 'PrimaryReferenceValue', displayName: 'Primary Reference Value', type: 'text', required: false },
      { name: 'PrimaryReferenceLocation', displayName: 'Primary Reference Location', type: 'text', required: false },
      
      // Secondary Reference Info (for searchability)
      { name: 'SecondaryReferenceCompany', displayName: 'Secondary Reference Company', type: 'text', required: false },
      { name: 'SecondaryReferencePhone', displayName: 'Secondary Reference Phone', type: 'text', required: false },
      { name: 'SecondaryReferenceValue', displayName: 'Secondary Reference Value', type: 'text', required: false },
      { name: 'SecondaryReferenceLocation', displayName: 'Secondary Reference Location', type: 'text', required: false },
      
      { name: 'Status', displayName: 'Approval Status', type: 'choice', required: false, choices: ['Pending', 'Approved', 'Rejected'] },
      { name: 'ApprovedBy', displayName: 'Approved By', type: 'text', required: false },
      { name: 'ApprovedDate', displayName: 'Approval Date', type: 'dateTime', required: false },
      { name: 'Recommendation', displayName: 'Recommendation Notes', type: 'text', required: false },
      
      // Individual Document URL columns
      { name: 'ApplicationLetterUrl', displayName: 'Application Letter URL', type: 'text', required: false },
      { name: 'IncorporationCertUrl', displayName: 'Certificate of Incorporation URL', type: 'text', required: false },
      { name: 'VatCertUrl', displayName: 'VAT Certificate URL', type: 'text', required: false },
      { name: 'PassportPhotoUrl', displayName: 'Passport Photograph URL', type: 'text', required: false },
      { name: 'PreviousJobEvidenceUrl', displayName: 'Previous Job Evidence URL', type: 'text', required: false },
      { name: 'TaxClearanceUrl', displayName: 'Tax Clearance Certificate URL', type: 'text', required: false },
      { name: 'PerformanceBondUrl', displayName: 'Performance Bond URL', type: 'text', required: false },
      { name: 'ReferenceLetterUrl', displayName: 'Reference Letters URL', type: 'text', required: false },
      { name: 'ContactPersonSignatureUrl', displayName: 'Contact Person Signature URL', type: 'text', required: false },
      { name: 'MematUrl', displayName: 'MEMAT Document URL', type: 'text', required: false },
      { name: 'IdentityDocumentsUrl', displayName: 'Identity Documents URL', type: 'text', required: false },
      { name: 'UtilityBillUrl', displayName: 'Utility Bill URL', type: 'text', required: false },
      { name: 'CacStatusReportUrl', displayName: 'CAC Status Report URL', type: 'text', required: false },
      
      // Contact Person Details
      { name: 'ContactPersonName', displayName: 'Contact Person Name', type: 'text', required: false },
      { name: 'ContactPersonPhone', displayName: 'Contact Person Phone', type: 'text', required: false },
      { name: 'ContactPersonDesignation', displayName: 'Contact Person Designation', type: 'text', required: false },
      { name: 'DeclarationAccepted', displayName: 'Declaration Accepted', type: 'yesNo', required: false },
      
      { name: 'VendorId', displayName: 'Vendor ID', type: 'text', required: false },
    ];

    for (const column of columns) {
      try {
        const columnData: any = {
          name: column.name,
          displayName: column.displayName || column.name,
          required: column.required,
        };

        // Set column type - Microsoft Graph API format
        if (column.type === 'text') {
          // Use larger maxLength for fields that might contain longer content
          const longTextFields = ['Categories', 'SpecializationDetails', 'BankingInfo', 'References', 'RegisteredAddress'];
          const maxLength = longTextFields.includes(column.name) ? 2000 : 255;
          columnData.text = {
            maxLength: maxLength
          };
        } else if (column.type === 'choice') {
          columnData.choice = {
            choices: column.choices || [],
            allowTextEntry: false
          };
        } else if (column.type === 'dateTime') {
          columnData.dateTime = {
            format: 'dateTime'
          };
        } else if (column.type === 'yesNo') {
          columnData.boolean = {};
        }
        
        // Add additional properties that might be required
        columnData.description = `Column for ${column.displayName}`;
        columnData.enforceUniqueValues = false;
        columnData.indexed = false;

        console.log(`Creating column: ${column.name} with data:`, JSON.stringify(columnData, null, 2));
        
        const response = await makeGraphCall(`/sites/${siteId}/lists/${listId}/columns`, {
          method: 'POST',
          body: JSON.stringify(columnData),
        });

        console.log(`✓ Column ${column.name} (${column.displayName}) added successfully:`, response);
      } catch (columnError: any) {
        // Column might already exist, which is fine
        const errorMessage = columnError.message || columnError.toString();
        if (errorMessage.includes('already exists') || errorMessage.includes('nameAlreadyExists')) {
          console.log(`Column ${column.name} already exists, skipping`);
        } else {
          console.warn(`Failed to add column ${column.name}:`, columnError);
        }
      }
    }
    
    console.log('All custom columns processed');
  } catch (error) {
    console.error('Error adding custom columns:', error);
  }
};

// Get list columns to validate structure
const getListColumns = async (siteId: string, listId: string) => {
  try {
    console.log('Getting list columns for validation...');
    const response = await makeGraphCall(`/sites/${siteId}/lists/${listId}/columns`);
    console.log('List columns:', JSON.stringify(response, null, 2));
    return response;
  } catch (error) {
    console.error('Error getting list columns:', error);
    return null;
  }
};

// Get SharePoint field type kind
const getFieldTypeKind = (type: string): number => {
  const fieldTypes: Record<string, number> = {
    'Text': 2,
    'Note': 3,
    'DateTime': 4,
    'Choice': 6,
  };
  return fieldTypes[type] || 2; // Default to Text
};

// Upload document to SharePoint document library using Microsoft Graph API
export const uploadDocumentToSharePoint = async (
  file: File, 
  vendorId: string, 
  documentType: string,
  companyName?: string
): Promise<{ success: boolean; url?: string; error?: string }> => {
  try {
    console.log('Uploading document using Microsoft Graph API...');
    console.log('File:', file.name, 'Size:', file.size, 'Type:', file.type);
    console.log('Vendor ID:', vendorId, 'Document Type:', documentType);
    
    // Get the site ID first
    const siteUrl = SHAREPOINT_CONFIG.siteUrl;
    const urlParts = new URL(siteUrl);
    const hostname = urlParts.hostname;
    const sitePath = urlParts.pathname.substring(1);
    const encodedSitePath = encodeURIComponent(sitePath);
    const siteResponse = await makeGraphCall(`/sites/${hostname}:/${encodedSitePath}`);
    const siteId = siteResponse.id;
    
    // Get the document library
    const docLibraryResponse = await makeGraphCall(`/sites/${siteId}/drives?$filter=name eq '${SHAREPOINT_CONFIG.documentLibrary}'`);
    
    if (!docLibraryResponse.value || docLibraryResponse.value.length === 0) {
      throw new Error(`Document library '${SHAREPOINT_CONFIG.documentLibrary}' not found`);
    }
    
    const driveId = docLibraryResponse.value[0].id;
    console.log('Document library drive ID:', driveId);
    
    // Create vendor folder if it doesn't exist
    // Use companyName-vendorId format if companyName is provided
    const folderName = companyName 
      ? `${sanitizeForSharePointFolder(companyName)}-${vendorId}`
      : vendorId;
    let vendorFolderId: string;
    try {
      const folderResponse = await makeGraphCall(`/drives/${driveId}/root:/${folderName}:`);
      vendorFolderId = folderResponse.id;
      console.log('Vendor folder already exists:', vendorFolderId);
    } catch (error) {
      // Create the vendor folder if it doesn't exist
      console.log('Creating vendor folder...');
      const createFolderResponse = await makeGraphCall(`/drives/${driveId}/root/children`, {
      method: 'POST',
        body: JSON.stringify({
          name: folderName,
          folder: {},
          '@microsoft.graph.conflictBehavior': 'rename'
        })
      });
      vendorFolderId = createFolderResponse.id;
      console.log('Vendor folder created:', vendorFolderId);
    }
    
    // Create document type folder if it doesn't exist
    let docFolderId: string;
    try {
      const docFolderResponse = await makeGraphCall(`/drives/${driveId}/root:/${folderName}/${documentType}:`);
      docFolderId = docFolderResponse.id;
      console.log(`${documentType} folder already exists:`, docFolderId);
    } catch (error) {
      // Create the document type folder
      const createDocFolderResponse = await makeGraphCall(`/drives/${driveId}/items/${vendorFolderId}/children`, {
        method: 'POST',
        body: JSON.stringify({
          name: documentType,
          folder: {},
          '@microsoft.graph.conflictBehavior': 'rename'
        })
      });
      docFolderId = createDocFolderResponse.id;
      console.log(`${documentType} folder created:`, docFolderId);
    }
    
    // Upload the file
    const fileName = file.name || `${documentType}.${file.type?.split('/')[1] || 'pdf'}`;
    const uploadResponse = await makeGraphCall(`/drives/${driveId}/items/${docFolderId}:/${fileName}:/content`, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream'
      }
    });
    
    // Get the web URL for the uploaded file
    const fileInfoResponse = await makeGraphCall(`/drives/${driveId}/items/${uploadResponse.id}`);
    const finalUrl = fileInfoResponse.webUrl;

    console.log('Document uploaded to SharePoint successfully:', finalUrl);
    return { success: true, url: finalUrl };
  } catch (error) {
    console.error('Error uploading document to SharePoint:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

// Ensure folder exists in SharePoint (using Microsoft Graph API)
const ensureFolderExists = async (folderPath: string): Promise<void> => {
  try {
    console.log('Folder creation handled by Microsoft Graph API in uploadDocumentToSharePoint');
    // This function is now handled by the Microsoft Graph API calls in uploadDocumentToSharePoint
  } catch (error) {
    // Folder might already exist, which is fine
    console.log('Folder creation result:', error);
  }
};

// Sanitize company name for use in SharePoint folder names
const sanitizeForSharePointFolder = (name: string): string => {
  return (name || '')
    .trim()
    .replace(/[<>:"/\\|?*]/g, '-') // Replace invalid SharePoint folder name characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
    .substring(0, 100); // SharePoint folder name limit
};

// Upload documents to SharePoint document library
export const uploadDocumentsToSharePoint = async (
  vendorId: string,
  firebaseFiles: Record<string, File>,
  siteId: string,
  companyName?: string
): Promise<{ success: boolean; error?: string; urls?: Record<string, string> }> => {
  try {
    console.log('Starting document upload to SharePoint...');
    console.log('Vendor ID:', vendorId);
    console.log('Files to upload:', Object.keys(firebaseFiles));
    
    const uploadedUrls: Record<string, string> = {};
    
    // Get the document library
    const docLibraryResponse = await makeGraphCall(`/sites/${siteId}/drives?$filter=name eq '${SHAREPOINT_CONFIG.documentLibrary}'`);
    
    if (!docLibraryResponse.value || docLibraryResponse.value.length === 0) {
      throw new Error(`Document library '${SHAREPOINT_CONFIG.documentLibrary}' not found`);
    }
    
    const driveId = docLibraryResponse.value[0].id;
    console.log('Document library drive ID:', driveId);
    
    // Create vendor folder if it doesn't exist
    // Use companyName-vendorId format if companyName is provided
    const folderName = companyName 
      ? `${sanitizeForSharePointFolder(companyName)}-${vendorId}`
      : vendorId;
    let vendorFolderId: string;
    
    try {
      // Try to get the vendor folder
      const folderResponse = await makeGraphCall(`/drives/${driveId}/root:/${folderName}:`);
      vendorFolderId = folderResponse.id;
      console.log('Vendor folder already exists:', vendorFolderId);
    } catch (error) {
      // Create the vendor folder if it doesn't exist
      console.log('Creating vendor folder...');
      const createFolderResponse = await makeGraphCall(`/drives/${driveId}/root/children`, {
        method: 'POST',
        body: JSON.stringify({
          name: folderName,
          folder: {},
          '@microsoft.graph.conflictBehavior': 'rename'
        })
      });
      vendorFolderId = createFolderResponse.id;
      console.log('Vendor folder created:', vendorFolderId);
    }
    
    // Upload each document
    for (const [docType, file] of Object.entries(firebaseFiles)) {
      try {
        console.log(`Uploading ${docType}...`);
        
        // Create document type folder
        let docFolderId: string;
        
        try {
          const docFolderResponse = await makeGraphCall(`/drives/${driveId}/root:/${folderName}/${docType}:`);
          docFolderId = docFolderResponse.id;
          console.log(`${docType} folder already exists:`, docFolderId);
        } catch (error) {
          // Create the document type folder
          const createDocFolderResponse = await makeGraphCall(`/drives/${driveId}/items/${vendorFolderId}/children`, {
            method: 'POST',
            body: JSON.stringify({
              name: docType,
              folder: {},
              '@microsoft.graph.conflictBehavior': 'rename'
            })
          });
          docFolderId = createDocFolderResponse.id;
          console.log(`${docType} folder created:`, docFolderId);
        }
        
        // Upload the file
        const fileName = file.name || `${docType}.${file.type?.split('/')[1] || 'pdf'}`;
        const uploadResponse = await makeGraphCall(`/drives/${driveId}/items/${docFolderId}:/${fileName}:/content`, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type || 'application/octet-stream'
          }
        });
        
        // Get the web URL for the uploaded file
        const fileInfoResponse = await makeGraphCall(`/drives/${driveId}/items/${uploadResponse.id}`);
        uploadedUrls[docType] = fileInfoResponse.webUrl;
        
        console.log(`✓ ${docType} uploaded successfully:`, uploadedUrls[docType]);
        
      } catch (error) {
        console.error(`Error uploading ${docType}:`, error);
        // Continue with other files even if one fails
      }
    }
    
    console.log('Document upload completed. Uploaded URLs:', uploadedUrls);
    return { success: true, urls: uploadedUrls };
    
  } catch (error) {
    console.error('Error uploading documents to SharePoint:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

// Migrate vendor data and files from Firebase to SharePoint
export const migrateVendorToSharePoint = async (
  vendor: any, 
  approvedBy: string,
  firebaseFiles: Record<string, File>
): Promise<{ success: boolean; error?: string; sharePointUrls?: Record<string, string> }> => {
  try {
    console.log('Starting vendor migration to SharePoint...');
    console.log('Vendor data received from Firebase:', JSON.stringify(vendor, null, 2));
    console.log('Approved by:', approvedBy);
    console.log('Firebase files count:', Object.keys(firebaseFiles).length);
    
    // Ensure list exists with columns
    const listResult = await ensureVendorListExists();
    if (!listResult.success) {
      throw new Error(`Failed to ensure list exists: ${listResult.error}`);
    }
    
    const sharePointUrls: Record<string, string> = {};
    
    // Upload documents to SharePoint if we have Firebase files
    if (firebaseFiles && Object.keys(firebaseFiles).length > 0) {
      console.log('Uploading documents to SharePoint...');
      const uploadResult = await uploadDocumentsToSharePoint(
        vendor.id, 
        firebaseFiles, 
        listResult.siteId!,
        vendor.companyName
      );
      if (uploadResult.success && uploadResult.urls) {
        Object.assign(sharePointUrls, uploadResult.urls);
        console.log('Documents uploaded successfully:', sharePointUrls);
      } else {
        console.warn('Document upload failed:', uploadResult.error);
        // Fallback to placeholder URLs if upload fails
    if (vendor.documentUrls) {
          const folderName = vendor.companyName 
            ? `${sanitizeForSharePointFolder(vendor.companyName)}-${vendor.id}`
            : vendor.id;
          for (const [docType, url] of Object.entries(vendor.documentUrls as Record<string, string>)) {
            if (url && typeof url === 'string') {
              const fileName = url.split('/').pop() || `${docType}.pdf`;
              sharePointUrls[docType] = `https://sunbethconcepts.sharepoint.com/sites/Procurement/Vendor Documents/${folderName}/${docType}/${fileName}`;
            }
          }
        }
      }
    } else if (vendor.documentUrls) {
      // If no Firebase files but we have document URLs, create placeholder SharePoint URLs
      const folderName = vendor.companyName 
        ? `${sanitizeForSharePointFolder(vendor.companyName)}-${vendor.id}`
        : vendor.id;
      for (const [docType, url] of Object.entries(vendor.documentUrls as Record<string, string>)) {
        if (url && typeof url === 'string') {
          const fileName = url.split('/').pop() || `${docType}.pdf`;
          sharePointUrls[docType] = `https://sunbethconcepts.sharepoint.com/sites/Procurement/Vendor Documents/${folderName}/${docType}/${fileName}`;
        }
      }
    }

    // Add vendor data to SharePoint list
    console.log('About to call addVendorToSharePointList with:', {
      siteId: listResult.siteId,
      listId: listResult.listId,
      vendorId: vendor.id,
      approvedBy
    });
    
    await addVendorToSharePointList(vendor, approvedBy, sharePointUrls, listResult.siteId!, listResult.listId!);

    console.log('Vendor migrated to SharePoint successfully');
    console.log('SharePoint URLs:', sharePointUrls);
    return { success: true, sharePointUrls };
  } catch (error) {
    console.error('Error migrating vendor to SharePoint:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

// Add vendor data to SharePoint list
const addVendorToSharePointList = async (
  vendor: any, 
  approvedBy: string, 
  sharePointUrls: Record<string, string>,
  siteId: string,
  listId: string
): Promise<void> => {
  try {
    console.log('Adding vendor data to SharePoint list...');
    
    // Ensure we have a valid vendor ID
    const vendorId = vendor.id || `vendor_${Date.now()}`;
    
    // Enhanced helper function to clean text data - applied to ALL text fields
    const cleanText = (text: string, maxLength: number = 255): string => {
      return String(text || '')
        .replace(/\\n/g, ' ') // Replace escaped newlines with spaces
        .replace(/\r?\n/g, ' ') // Replace actual newlines with spaces
        .replace(/- /g, '• ') // Replace "- " with bullet points for better readability
        .replace(/\s+/g, ' ') // Replace multiple spaces with single space
        .replace(/[^\x20-\x7E]/g, '') // Remove non-ASCII characters
        .replace(/[^\w\s•.,;:!?@#$%&*()+=<>[\]{}|\\/`~'"-]/g, '') // Remove additional special characters that might cause issues
        .trim()
        .substring(0, maxLength);
    };
    
    // Special function to clean References field with aggressive truncation
    const cleanReferences = (references: any[]): string => {
      if (!references || !Array.isArray(references)) {
        return '';
      }
      
      // Create a simplified, clean version of references
      const cleanedRefs = references.map((ref: any, index: number) => {
        const company = cleanText(ref.company || '', 50);
        const contact = cleanText(ref.clientContact || '', 30);
        const value = cleanText(ref.contractValue || '', 20);
        const location = cleanText(ref.location || '', 30);
        
        return `${index + 1}. ${company} - ${contact} (${value}) - ${location}`;
      });
      
      // Join with semicolons and truncate to 200 characters to be safe
      const result = cleanedRefs.join('; ');
      return result.substring(0, 200);
    };
    
    // Special function to clean SpecializationDetails with enhanced handling
    const cleanSpecializationDetails = (text: string): string => {
      return String(text || '')
        .replace(/\\n/g, ' ') // Replace escaped newlines with spaces
        .replace(/\r?\n/g, ' ') // Replace actual newlines with spaces
        .replace(/- /g, '• ') // Replace "- " with bullet points for better readability
        .replace(/\s+/g, ' ') // Replace multiple spaces with single space
        .replace(/[^\x20-\x7E]/g, '') // Remove non-ASCII characters
        .replace(/[^\w\s•.,;:!?@#$%&*()+=<>[\]{}|\\/`~'"-]/g, '') // Remove additional special characters that might cause issues
        .trim()
        .substring(0, 2000); // Use longer limit for specialization details
    };
    
    // Prepare vendor data with clean, validated fields
    const vendorData: any = {
      Title: cleanText(vendor.companyName || 'Unknown Company', 255),
      Status: 'Approved',
      ApprovedBy: cleanText(approvedBy || 'System', 255),
      VendorId: String(vendorId),
      ApprovedDate: new Date().toISOString()
    };

    // Add basic vendor information
    if (vendor.companyName) vendorData.CompanyName = cleanText(vendor.companyName, 255);
    if (vendor.email) vendorData.Email = cleanText(vendor.email, 255);
    if (vendor.telephone) vendorData.Telephone = cleanText(vendor.telephone, 255);
    if (vendor.registrationNo) vendorData.RegistrationNumber = cleanText(vendor.registrationNo, 255);
    if (vendor.tinNo) vendorData.TINNumber = cleanText(vendor.tinNo, 255);
    if (vendor.registeredAddress) vendorData.RegisteredAddress = cleanText(vendor.registeredAddress, 2000);
    
    // Handle categories
    if (vendor.categories) {
      const categoryKeys = Object.keys(vendor.categories).filter(key => vendor.categories[key]);
      if (categoryKeys.length > 0) {
        // Clean each category key before joining
        const cleanedCategoryKeys = categoryKeys.map(key => cleanText(key, 100));
        vendorData.Categories = cleanText(cleanedCategoryKeys.join(', '), 2000);
      }
    }
    
    // Handle specialization details with enhanced cleaning
    if (vendor.specializationDetails) {
      vendorData.SpecializationDetails = cleanSpecializationDetails(vendor.specializationDetails);
    }
    
    // Handle Credit Information
    if (vendor.extendCredit) {
      vendorData.ExtendCredit = cleanText(vendor.extendCredit, 255);
    }
    if (vendor.creditVolume) {
      vendorData.CreditVolume = cleanText(vendor.creditVolume, 255);
    }
    if (vendor.creditTenure) {
      vendorData.CreditTenure = cleanText(vendor.creditTenure, 255);
    }
    if (vendor.paymentPlan) {
      vendorData.PaymentPlan = cleanText(vendor.paymentPlan, 255);
    }
    if (vendor.postDeliveryPayment) {
      vendorData.PostDeliveryPayment = cleanText(vendor.postDeliveryPayment, 255);
    }
    
    // Handle Contact Person Details
    if (vendor.contactPersonName) {
      vendorData.ContactPersonName = cleanText(vendor.contactPersonName, 255);
    }
    if (vendor.contactPersonPhone) {
      vendorData.ContactPersonPhone = cleanText(vendor.contactPersonPhone, 255);
    }
    if (vendor.contactPersonDesignation) {
      vendorData.ContactPersonDesignation = cleanText(vendor.contactPersonDesignation, 255);
    }
    if (vendor.declarationAccepted !== undefined) {
      vendorData.DeclarationAccepted = vendor.declarationAccepted;
    }
    
    // Handle Banking Info - Store as JSON and extract key fields
    if (vendor.bankingInfo) {
      // Clean banking info data before JSON stringification
      const cleanedBankingInfo = vendor.bankingInfo.map((bank: any) => ({
        bankName: cleanText(bank.bankName || '', 255),
        accountNumber: cleanText(bank.accountNumber || '', 255),
        accountType: cleanText(bank.accountType || '', 255)
      }));
      
      const bankingJson = JSON.stringify(cleanedBankingInfo);
      vendorData.BankingInfo = bankingJson.length > 1950 ? bankingJson.substring(0, 1950) + '...' : bankingJson;
      
      // Extract primary bank details
      if (vendor.bankingInfo.length > 0) {
        const primaryBank = vendor.bankingInfo[0];
        vendorData.PrimaryBankName = cleanText(primaryBank.bankName || '', 255);
        vendorData.PrimaryBankType = cleanText(primaryBank.accountType || '', 255);
        vendorData.PrimaryAccountNumber = cleanText(primaryBank.accountNumber || '', 255);
      }
      
      // Extract secondary bank details
      if (vendor.bankingInfo.length > 1) {
        const secondaryBank = vendor.bankingInfo[1];
        vendorData.SecondaryBankName = cleanText(secondaryBank.bankName || '', 255);
        vendorData.SecondaryBankType = cleanText(secondaryBank.accountType || '', 255);
        vendorData.SecondaryAccountNumber = cleanText(secondaryBank.accountNumber || '', 255);
      }
    }
    
    // Handle BVN
    if (vendor.bvn) {
      vendorData.BVN = cleanText(vendor.bvn, 11); // BVN is exactly 11 digits
    }
    
    // Handle References with special cleaning and truncation
    if (vendor.references) {
      vendorData.References = cleanReferences(vendor.references);
      
      // Extract primary reference details
      if (vendor.references.length > 0) {
        const primaryRef = vendor.references[0];
        vendorData.PrimaryReferenceCompany = cleanText(primaryRef.company || '', 255);
        vendorData.PrimaryReferencePhone = cleanText(primaryRef.telNo || '', 255);
        vendorData.PrimaryReferenceValue = cleanText(primaryRef.contractValue || '', 255);
        vendorData.PrimaryReferenceLocation = cleanText(primaryRef.location || '', 255);
      }
      
      // Extract secondary reference details
      if (vendor.references.length > 1) {
        const secondaryRef = vendor.references[1];
        vendorData.SecondaryReferenceCompany = cleanText(secondaryRef.company || '', 255);
        vendorData.SecondaryReferencePhone = cleanText(secondaryRef.telNo || '', 255);
        vendorData.SecondaryReferenceValue = cleanText(secondaryRef.contractValue || '', 255);
        vendorData.SecondaryReferenceLocation = cleanText(secondaryRef.location || '', 255);
      }
    }
    
    // Handle recommendation
    if (vendor.notes) vendorData.Recommendation = cleanText(vendor.notes, 255);
    
    // Handle individual document URLs
    if (Object.keys(sharePointUrls).length > 0) {
      const documentFieldMapping: Record<string, string> = {
        'applicationLetter': 'ApplicationLetterUrl',
        'incorporationCert': 'IncorporationCertUrl',
        'vatCert': 'VatCertUrl',
        'passportPhoto': 'PassportPhotoUrl',
        'previousJobEvidence': 'PreviousJobEvidenceUrl',
        'taxClearance': 'TaxClearanceUrl',
        'performanceBond': 'PerformanceBondUrl',
        'referenceLetter': 'ReferenceLetterUrl',
        'contactPersonSignature': 'ContactPersonSignatureUrl',
        'memat': 'MematUrl',
        'identityDocuments': 'IdentityDocumentsUrl',
        'utilityBill': 'UtilityBillUrl',
        'cacStatusReport': 'CacStatusReportUrl'
      };
      
      for (const [docType, url] of Object.entries(sharePointUrls)) {
        const fieldName = documentFieldMapping[docType];
        if (fieldName && url) {
          vendorData[fieldName] = cleanText(url, 255);
        }
      }
    }

    console.log('Submitting vendor data to SharePoint...');
    console.log('Vendor data:', JSON.stringify(vendorData, null, 2));
    
    // Single submission with all validated data
    const response = await makeGraphCall(`/sites/${siteId}/lists/${listId}/items`, {
      method: 'POST',
      body: JSON.stringify({
        fields: vendorData
      }),
    });

    console.log('✓ Vendor data added to SharePoint successfully:', response);
  } catch (error) {
    console.error('Error adding vendor data to SharePoint list:', error);
    throw error;
  }
};

// Add approved vendor to SharePoint list (legacy function for backward compatibility)
export const addVendorToSharePoint = async (vendor: any, approvedBy: string): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('Adding vendor to SharePoint (placeholder) - returning success');
    console.log('Vendor added to SharePoint successfully (placeholder)');
    return { success: true };
  } catch (error) {
    console.error('Error adding vendor to SharePoint:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

// Update vendor status in SharePoint
export const updateVendorStatusInSharePoint = async (
  vendorId: string, 
  status: string, 
  approvedBy: string,
  recommendation?: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('Updating vendor status in SharePoint (placeholder) - returning success');
    console.log('Vendor status updated in SharePoint successfully (placeholder)');
    return { success: true };
  } catch (error) {
    console.error('Error updating vendor status in SharePoint:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

// Get all vendors from SharePoint
export const getVendorsFromSharePoint = async (): Promise<{ success: boolean; vendors?: any[]; error?: string }> => {
  try {
    console.log('Getting vendors from SharePoint (placeholder) - returning empty array');
    return { success: true, vendors: [] };
  } catch (error) {
    console.error('Error getting vendors from SharePoint:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

// Sync all approved vendors from Firebase to SharePoint
export const syncApprovedVendorsToSharePoint = async (
  vendors: any[], 
  accessToken: string
): Promise<{ success: boolean; synced: number; errors: string[] }> => {
  try {
    await initializeSharePoint(accessToken);
    await ensureVendorListExists();

    const approvedVendors = vendors.filter(vendor => vendor.status === 'approved');
    let synced = 0;
    const errors: string[] = [];

    for (const vendor of approvedVendors) {
      try {
        const result = await addVendorToSharePoint(vendor, 'System Sync');
        if (result.success) {
          synced++;
        } else {
          errors.push(`Failed to sync vendor ${vendor.companyName}: ${result.error}`);
        }
      } catch (error) {
        errors.push(`Error syncing vendor ${vendor.companyName}: ${error}`);
      }
    }

    return { success: true, synced, errors };
  } catch (error) {
    console.error('Error syncing vendors to SharePoint:', error);
    return { 
      success: false, 
      synced: 0, 
      errors: [error instanceof Error ? error.message : 'Unknown error'] 
    };
  }
};